import { GQL_ID, PageInfo, Product } from '@@types'
import { gql, useQuery } from '@apollo/client'

import { SHORTCUT_PRODUCT_CARD_FIELDS, SHORTCUT_PRODUCT_FIELDS, SHORTCUT_PRODUCT_SHOP_INFO } from '@shared/api/shortcuts'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'

export interface SaleProductsData {
  saleProducts: {
    pageInfo: PageInfo
    edges: Product[]
  }
}

export interface SaleProductsVariables {
  shop_id: GQL_ID
  style?: string
  page?: number
  limit?: number
}

export const GQL_QUERY_SALE_PRODUCTS = gql`
  query saleProducts (
    $shop_id: ID!
    $style: String
    $page: Int
    $limit: Int
  ) {
    saleProducts(
      shop_id: $shop_id,
      _page: $page,
      _limit: $limit
    ) {
      pageInfo {
        page
        perPage
        lastPage
        next
      }
      edges {
        ${SHORTCUT_PRODUCT_FIELDS}
        ${SHORTCUT_PRODUCT_SHOP_INFO}
        ${SHORTCUT_PRODUCT_CARD_FIELDS}
      }
    }
  }
`

export const useSaleProducts = (variables: SaleProductsVariables) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<SaleProductsData, SaleProductsVariables>(
    GQL_QUERY_SALE_PRODUCTS,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      skip: !variables.shop_id
    }
  )

  const saleProducts = data?.saleProducts?.edges ?? []

  const { hasMore, loadingMore, fetchNextPage } =
    useFetchMorePagination<
      SaleProductsData,
      SaleProductsVariables
    >({
      loading,
      fetchMore,
      pageVariable: 'page',
      usePager: false,
      variables: {
        shop_id: variables.shop_id,
        limit: variables.limit
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.saleProducts?.edges ?? []
        const curr = fetched?.saleProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            saleProducts: {
              ...cached.saleProducts,
              edges: [ ...prev, ...curr ],
            },
          },
        }
      },
    })

  
  return {
    saleProducts,
    data,
    loading,
    error,
    refetch,
    hasMore,
    loadingMore,
    fetchNextPage,
    fetchMore
  }
}
